// Linha de transacao em listagens. Layout horizontal:
//   [icone tipo] Titulo + (categoria · grupo · data) ........ valor
//
// Cor do amount derivada do `type`: receita verde, despesa em on-surface
// (negativo soa "frio" mas mantem hierarquia visual com saldo geral).
//
// Espelha o item do mobile (`grana-money-app/src/features/transactions/components/transaction-item.tsx`).

import { ArrowDownRight, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { TransactionItem as Tx } from '../types/transactions.types';

import { cn } from '@/shared/lib/cn';
import { formatBRL } from '@/shared/lib/currency';
import { formatRelativeBR } from '@/shared/lib/date';

export interface TransactionItemProps {
  tx: Tx;
  testID?: string;
}

export function TransactionItem({ tx, testID }: TransactionItemProps) {
  const isExpense = tx.type === 'expense';
  const Icon = isExpense ? ArrowDownRight : ArrowUpRight;
  const detailHref = isExpense ? `/expenses/${tx.id}` : `/incomes/${tx.id}`;

  const subtitleParts = [tx.category, tx.familyGroup, formatRelativeBR(tx.date)]
    .filter(Boolean)
    .join(' · ');

  return (
    <Link
      to={detailHref}
      data-testid={testID}
      className={cn(
        'flex items-center gap-3 rounded-card px-3 py-3 transition-colors hover:bg-surface-high',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
      )}
    >
      <span
        aria-hidden
        className={cn(
          'inline-flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-base',
          isExpense ? 'bg-danger/10 text-danger' : 'bg-primary/10 text-primary',
        )}
      >
        <Icon size={18} strokeWidth={2} />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-body-md font-semibold text-on-surface">{tx.title}</p>
        {subtitleParts ? (
          <p className="truncate text-label-md text-secondary-text">{subtitleParts}</p>
        ) : null}
      </div>
      <span
        className={cn(
          'flex-shrink-0 text-body-md font-bold tabular-nums',
          isExpense ? 'text-on-surface' : 'text-primary',
        )}
      >
        {isExpense ? '-' : '+'} {formatBRL(tx.amount)}
      </span>
    </Link>
  );
}
